import { useMemo, useState } from "react";
import { Button, Card, FormLayout, Text, TextField } from "@shopify/polaris";
import { getAdminTimeOffsetMinutes, setAdminTimeOffsetMinutes } from "./utils/dateTime";

const minutesToHourString = (minutes: number) => {
  const hours = minutes / 60;
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(2);
};

export default function AdminSystemPage() {
  const [offsetHours, setOffsetHours] = useState(minutesToHourString(getAdminTimeOffsetMinutes()));
  const [status, setStatus] = useState<string | null>(null);

  const parsedOffsetMinutes = useMemo(() => {
    const parsed = Number(offsetHours);
    if (!Number.isFinite(parsed)) return null;

    return Math.round(parsed * 60);
  }, [offsetHours]);

  const now = new Date();
  const adjustedNow = parsedOffsetMinutes === null
    ? null
    : new Date(now.getTime() + parsedOffsetMinutes * 60_000);

  const saveOffset = () => {
    if (parsedOffsetMinutes === null) {
      setStatus("Enter a valid number of hours, for example -3, 2, or 5.5.");
      return;
    }

    setAdminTimeOffsetMinutes(parsedOffsetMinutes);
    setOffsetHours(minutesToHourString(parsedOffsetMinutes));
    setStatus("Saved. Admin console date/time values now use this offset.");
  };

  const clearOffset = () => {
    setAdminTimeOffsetMinutes(0);
    setOffsetHours("0");
    setStatus("Offset reset to 0 hours.");
  };

  return (
    <Card>
      <FormLayout>
        <Text as="h3" variant="headingMd">System settings</Text>
        <Text as="p" variant="bodyMd">
          Date/time values in the PF Console are based on server timestamps. Use an offset to align them with your local time.
        </Text>
        <TextField
          label="Date/time offset (hours)"
          type="number"
          value={offsetHours}
          onChange={setOffsetHours}
          autoComplete="off"
          helpText="Examples: -3, 2, 5.5. A positive value moves times forward."
        />
        <Text as="p" variant="bodySm">Current browser time: {now.toLocaleString()}</Text>
        <Text as="p" variant="bodySm">Preview with offset: {adjustedNow ? adjustedNow.toLocaleString() : "Invalid offset"}</Text>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Button variant="primary" onClick={saveOffset}>Save offset</Button>
          <Button onClick={clearOffset}>Reset to 0</Button>
        </div>
        {status ? <Text as="p" tone={parsedOffsetMinutes === null ? "critical" : "success"}>{status}</Text> : null}
      </FormLayout>
    </Card>
  );
}
